import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Sparkles, ChevronRight, Info, Download, Crown, Lock, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { calculateDasha, generateDashaReport, getReportUsage, createCheckout, purchaseAdditionalReport } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { trackEvent } from "@/lib/useAnalytics";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function DashaForecast() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: "",
    birth_date: "",
    birth_time: "",
    birth_city: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [usage, setUsage] = useState(null);

  const updateField = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  // Fetch usage on mount
  useEffect(() => {
    if (user) {
      getReportUsage().then(setUsage).catch(console.error);
    }
  }, [user]);

  const isPremium = usage?.is_premium;
  const canGenerateFree = usage?.can_generate_free;

  const handleCalculate = async () => {
    const required = ["birth_date", "birth_time", "birth_city"];
    for (const key of required) {
      if (!form[key]?.trim()) {
        toast.error("Please fill in all required fields");
        return;
      }
    }

    if (!isPremium) {
      toast.error("Dasha Forecast requires Premium subscription or single purchase.");
      return;
    }

    if (!canGenerateFree) {
      toast.error(`Monthly allowance used. Additional reports: $1.00 each.`);
      return;
    }

    setLoading(true);
    try {
      trackEvent("dasha_forecast_run");
      const data = await calculateDasha(form);
      setResult(data);
    } catch (e) {
      if (e.response?.status === 402) {
        const detail = e.response?.data?.detail;
        toast.error(detail?.message || "Payment required for this report");
      } else {
        toast.error("Calculation failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    trackEvent("checkout_started", { report: "dasha_forecast" });
    try {
      const checkout = await createCheckout({
        package_id: "dasha_forecast",
        origin_url: window.location.origin,
        metadata: form,
      });
      if (checkout.url) window.location.href = checkout.url;
    } catch {
      toast.error("Failed to start checkout");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors" data-testid="dasha-back-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm tracking-wider uppercase">Back</span>
          </Link>
        </div>
      </nav>

      <main className="pt-14 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="w-6 h-6 text-amber-400/80" />
            <h1 className="text-4xl sm:text-5xl font-light text-white/95" data-testid="dasha-title">Dasha Forecast</h1>
          </div>
          <p className="text-white/40 text-sm mb-4">
            Vimshottari Mahadasha & Antardasha — your current planetary period and what it activates.
          </p>
          
          {/* Access indicator */}
          <div className="flex items-center gap-2 mb-10">
            {isPremium ? (
              <>
                <Crown className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm" data-testid="premium-indicator">
                  Premium — {usage?.remaining_included || 0} included{(usage?.additional_credits || 0) > 0 ? ` + ${usage.additional_credits} purchased` : ""} remaining
                </span>
              </>
            ) : (
              <>
                <Lock className="w-4 h-4 text-white/40" />
                <span className="text-white/40 text-sm" data-testid="locked-indicator">
                  Premium or single purchase required
                </span>
              </>
            )}
          </div>
        </motion.div>

        {!result ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
            <div
              className="rounded-lg p-6 mb-6"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
            >
              <div className="space-y-5">
                <div>
                  <Label className="text-white/40 text-xs tracking-[0.15em] uppercase">Name (optional)</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => updateField("name", e.target.value)}
                    placeholder="Your name"
                    className="bg-transparent border-0 border-b border-white/20 rounded-none text-white/90 placeholder:text-white/15 focus:border-white/60 focus-visible:ring-0 focus-visible:ring-offset-0 h-11"
                    data-testid="dasha-name"
                  />
                </div>
                <div>
                  <Label className="text-white/40 text-xs tracking-[0.15em] uppercase">Birth Date *</Label>
                  <div className="relative">
                    <Calendar className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                    <Input
                      type="date"
                      value={form.birth_date}
                      onChange={(e) => updateField("birth_date", e.target.value)}
                      className="bg-transparent border-0 border-b border-white/20 rounded-none pl-6 text-white/90 focus:border-white/60 focus-visible:ring-0 focus-visible:ring-offset-0 h-11"
                      data-testid="dasha-date"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-white/40 text-xs tracking-[0.15em] uppercase">Birth Time *</Label>
                  <div className="relative">
                    <Clock className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                    <Input
                      type="time"
                      value={form.birth_time}
                      onChange={(e) => updateField("birth_time", e.target.value)}
                      className="bg-transparent border-0 border-b border-white/20 rounded-none pl-6 text-white/90 focus:border-white/60 focus-visible:ring-0 focus-visible:ring-offset-0 h-11"
                      data-testid="dasha-time"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-white/40 text-xs tracking-[0.15em] uppercase">Birth City *</Label>
                  <Input
                    value={form.birth_city}
                    onChange={(e) => updateField("birth_city", e.target.value)}
                    placeholder="e.g. Mumbai, New York"
                    className="bg-transparent border-0 border-b border-white/20 rounded-none text-white/90 placeholder:text-white/15 focus:border-white/60 focus-visible:ring-0 focus-visible:ring-offset-0 h-11"
                    data-testid="dasha-city"
                  />
                </div>
              </div>
            </div>

            {isPremium ? (
              canGenerateFree ? (
                <Button
                  onClick={handleCalculate}
                  disabled={loading}
                  className="w-full bg-white text-black hover:bg-white/90 rounded-full h-12 text-sm tracking-wider font-medium"
                  data-testid="dasha-calculate-btn"
                >
                  {loading ? "Calculating..." : "Calculate Dasha Forecast"}
                </Button>
              ) : (
                <div className="space-y-3">
                  <Button
                    onClick={async () => {
                      trackEvent("checkout_started", { report: "additional_report", type: "dasha_forecast" });
                      try {
                        const checkout = await purchaseAdditionalReport("dasha_forecast");
                        if (checkout.url) window.location.href = checkout.url;
                      } catch {
                        toast.error("Failed to start checkout");
                      }
                    }}
                    className="w-full bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30 rounded-full h-12 text-sm tracking-wider font-medium"
                    data-testid="dasha-purchase-additional-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Purchase Additional Report — $1.00
                  </Button>
                  <p className="text-white/30 text-xs text-center">Monthly allowance used. Resets {usage?.reset_date}</p>
                </div>
              )
            ) : (
              <div className="space-y-3">
                <Button
                  onClick={handlePurchase}
                  className="w-full bg-white text-black hover:bg-white/90 rounded-full h-12 text-sm tracking-wider font-medium"
                  data-testid="dasha-purchase-btn"
                >
                  Purchase Dasha Forecast — $1.99
                </Button>
                <Link to="/pricing" className="block">
                  <Button
                    variant="outline"
                    className="w-full border-amber-500/20 text-amber-400 hover:bg-amber-500/10 rounded-full h-12 text-sm"
                    data-testid="dasha-subscribe-btn"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Subscribe to Premium — $4.29/mo
                  </Button>
                </Link>
              </div>
            )}

            {/* Info section */}
            <div className="mt-8 p-4 rounded-lg bg-white/[0.02] border border-white/5">
              <div className="flex items-start gap-3">
                <Info className="w-4 h-4 text-white/30 mt-0.5" />
                <div className="text-white/40 text-xs leading-relaxed">
                  <p className="mb-2"><strong className="text-white/60">What is Vimshottari Dasha?</strong></p>
                  <p>The Vimshottari system divides life into 120-year planetary cycles based on your natal Moon's nakshatra. 
                  Each <strong className="text-white/60">Mahadasha</strong> (major period) is ruled by a planet and lasts years. 
                  Within it, <strong className="text-white/60">Antardashas</strong> (sub-periods) further refine the activated energies.</p>
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <DashaResult result={result} form={form} showTimeline={showTimeline} setShowTimeline={setShowTimeline} usage={usage} setUsage={setUsage} isPremium={isPremium} />
        )}
      </main>
    </div>
  );
}

function DashaResult({ result, form, showTimeline, setShowTimeline, usage, setUsage, isPremium }) {
  const md = result.current_mahadasha;
  const ad = result.current_antardasha;
  const interp = result.interpretation;
  const [downloading, setDownloading] = useState(false);
  
  const canGenerateFree = usage?.can_generate_free;

  const handleDownloadPDF = async () => {
    if (!isPremium) {
      toast.error("PDF download requires Premium subscription");
      return;
    }

    if (!canGenerateFree) {
      toast.error("No reports remaining. Purchase additional credits first.");
      return;
    }

    setDownloading(true);
    try {
      const blob = await generateDashaReport({
        name: form.name || result.natal?.name || "Seeker",
        birth_date: form.birth_date || result.natal?.birth_date,
        birth_time: form.birth_time || result.natal?.birth_time,
        birth_city: form.birth_city || result.natal?.birth_city,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dasha_forecast_${md.planet.toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success("PDF downloaded!");
      // Refresh usage
      getReportUsage().then(setUsage).catch(console.error);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Additional payment required");
        getReportUsage().then(setUsage).catch(console.error);
      } else {
        toast.error("Failed to generate PDF");
      }
    } finally {
      setDownloading(false);
    }
  };
  
  const handlePurchaseAdditional = async () => {
    trackEvent("checkout_started", { report: "additional_report", type: "dasha_forecast" });
    try {
      const checkout = await purchaseAdditionalReport("dasha_forecast");
      if (checkout.url) window.location.href = checkout.url;
    } catch {
      toast.error("Failed to start checkout");
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      {/* Download PDF Button (Premium only) */}
      {isPremium && (
        <div className="mb-6 flex justify-between items-center flex-wrap gap-3">
          <div className="text-sm text-white/40">
            {usage?.remaining_included || 0} included{(usage?.additional_credits || 0) > 0 ? ` + ${usage.additional_credits} purchased` : ""} remaining
          </div>
          {canGenerateFree ? (
            <Button
              onClick={handleDownloadPDF}
              disabled={downloading}
              variant="outline"
              className="border-amber-500/30 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 rounded-full"
              data-testid="dasha-download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {downloading ? "Generating PDF..." : "Download PDF"}
            </Button>
          ) : (
            <Button
              onClick={handlePurchaseAdditional}
              variant="outline"
              className="border-amber-500/30 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 rounded-full"
              data-testid="dasha-purchase-additional-pdf-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Purchase Additional — $1.00
            </Button>
          )}
        </div>
      )}

      {/* Natal Info Header */}
      {result.natal && (
        <div className="mb-6 p-4 rounded-lg bg-white/[0.02] border border-white/5">
          <p className="text-white/50 text-xs tracking-[0.15em] uppercase mb-2">Birth Moon</p>
          <p className="text-white/80 text-sm">
            <span className="text-white/90 font-medium">{result.natal.nakshatra_name}</span>
            <span className="text-white/40"> • Pada {result.natal.pada} • {result.natal.moon_sign}</span>
          </p>
          <p className="text-white/40 text-xs mt-1">Ruled by {result.natal.ruler}</p>
        </div>
      )}

      {/* Current Mahadasha Card */}
      <div
        className="rounded-lg p-6 mb-4"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
        data-testid="dasha-mahadasha-card"
      >
        <p className="text-white/50 text-xs tracking-[0.15em] uppercase mb-3">Current Mahadasha</p>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center">
            <span className="text-2xl text-amber-400">{md.symbol}</span>
          </div>
          <div>
            <p className="text-white/90 text-xl font-light">{md.planet}</p>
            <p className="text-white/40 text-xs">
              {md.start_date} → {md.end_date}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="text-white/50">
            <span className="text-white/70 font-medium">{md.years_remaining}</span> years remaining
          </div>
          <div className="text-white/30">|</div>
          <div className="text-white/50">
            <span className="text-white/70 font-medium">{md.days_remaining}</span> days
          </div>
        </div>
      </div>

      {/* Current Antardasha Card */}
      <div
        className="rounded-lg p-6 mb-4"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
        data-testid="dasha-antardasha-card"
      >
        <p className="text-white/50 text-xs tracking-[0.15em] uppercase mb-3">Current Antardasha</p>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
            <span className="text-xl text-blue-400">{ad.symbol}</span>
          </div>
          <div>
            <p className="text-white/90 text-lg font-light">{ad.planet}</p>
            <p className="text-white/40 text-xs">
              {ad.start_date} → {ad.end_date}
            </p>
          </div>
        </div>
        <div className="text-xs text-white/50">
          <span className="text-white/70 font-medium">{ad.days_remaining}</span> days remaining
        </div>
      </div>

      {/* Activated Planets */}
      <div
        className="rounded-lg p-5 mb-6"
        style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)" }}
      >
        <p className="text-white/50 text-xs tracking-[0.15em] uppercase mb-3">Activated Planets</p>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-amber-500/10 border border-amber-500/20">
            <span className="text-amber-400">{result.activated_planets.primary_symbol}</span>
            <span className="text-white/70 text-sm">{result.activated_planets.primary}</span>
            <span className="text-white/30 text-xs">(primary)</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-blue-500/10 border border-blue-500/20">
            <span className="text-blue-400">{result.activated_planets.secondary_symbol}</span>
            <span className="text-white/70 text-sm">{result.activated_planets.secondary}</span>
            <span className="text-white/30 text-xs">(secondary)</span>
          </div>
        </div>
      </div>

      {/* Interpretation */}
      <div
        className="rounded-lg p-6 mb-6"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
        data-testid="dasha-interpretation"
      >
        <p className="text-white/50 text-xs tracking-[0.15em] uppercase mb-4">What This Activates</p>
        <p className="text-white/60 text-sm mb-6 leading-relaxed">{interp.era_description}</p>

        <div className="space-y-5">
          <div>
            <p className="text-white/40 text-xs tracking-[0.1em] uppercase mb-2">Themes</p>
            <ul className="space-y-1">
              {interp.themes.map((t, i) => (
                <li key={i} className="text-white/70 text-sm flex items-start gap-2">
                  <span className="text-amber-400/60 mt-1">•</span>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-white/40 text-xs tracking-[0.1em] uppercase mb-2">Supportive Actions</p>
            <ul className="space-y-1">
              {interp.supportive_actions.map((a, i) => (
                <li key={i} className="text-white/70 text-sm flex items-start gap-2">
                  <span className="text-emerald-400/60 mt-1">✓</span>
                  {a}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-white/40 text-xs tracking-[0.1em] uppercase mb-2">Watch-Outs</p>
            <ul className="space-y-1">
              {interp.watch_outs.map((w, i) => (
                <li key={i} className="text-white/70 text-sm flex items-start gap-2">
                  <span className="text-red-400/60 mt-1">!</span>
                  {w}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Timeline Toggle */}
      <Button
        variant="ghost"
        onClick={() => setShowTimeline(!showTimeline)}
        className="w-full text-white/50 hover:text-white/80 hover:bg-white/5 mb-4"
        data-testid="dasha-timeline-toggle"
      >
        {showTimeline ? "Hide" : "View"} Dasha Timeline
        <ChevronRight className={`w-4 h-4 ml-2 transition-transform ${showTimeline ? "rotate-90" : ""}`} />
      </Button>

      {/* Timeline */}
      {showTimeline && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          transition={{ duration: 0.3 }}
        >
          {/* Mahadasha Timeline */}
          <div className="mb-6">
            <p className="text-white/50 text-xs tracking-[0.15em] uppercase mb-3">Mahadasha Timeline</p>
            <div className="space-y-2">
              {result.mahadasha_timeline.map((d, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    d.is_current
                      ? "bg-amber-500/10 border border-amber-500/20"
                      : "bg-white/[0.02] border border-white/5"
                  }`}
                >
                  <span className={`text-lg ${d.is_current ? "text-amber-400" : "text-white/40"}`}>{d.symbol}</span>
                  <div className="flex-1">
                    <p className={`text-sm ${d.is_current ? "text-white/90" : "text-white/60"}`}>{d.planet}</p>
                    <p className="text-white/30 text-xs">{d.start_date} → {d.end_date}</p>
                  </div>
                  <span className="text-white/40 text-xs">{d.duration_years}y</span>
                </div>
              ))}
            </div>
          </div>

          {/* Antardasha Timeline within current Mahadasha */}
          <div>
            <p className="text-white/50 text-xs tracking-[0.15em] uppercase mb-3">
              Antardashas in {md.planet} Mahadasha
            </p>
            <div className="space-y-2">
              {result.all_antardashas.map((ad, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    ad.is_current
                      ? "bg-blue-500/10 border border-blue-500/20"
                      : "bg-white/[0.02] border border-white/5"
                  }`}
                >
                  <span className={`text-lg ${ad.is_current ? "text-blue-400" : "text-white/40"}`}>{ad.symbol}</span>
                  <div className="flex-1">
                    <p className={`text-sm ${ad.is_current ? "text-white/90" : "text-white/60"}`}>{ad.planet}</p>
                    <p className="text-white/30 text-xs">{ad.start_date} → {ad.end_date}</p>
                  </div>
                  <span className="text-white/40 text-xs">{Math.round(ad.duration_days / 30)}mo</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Calculate Again */}
      <div className="mt-8 text-center">
        <Link to="/dasha" onClick={() => window.location.reload()}>
          <Button variant="outline" className="border-white/10 text-white/60 hover:text-white hover:bg-white/5 rounded-full">
            Calculate for Another Person
          </Button>
        </Link>
      </div>
    </motion.div>
  );
}
